/**
 * Defensive UI formatter for `review_requests.last_error`.
 *
 * The edge function now stores friendly, user-safe strings in `last_error`,
 * but historical rows may still contain raw provider payloads such as
 * `Resend 422: {"statusCode":422,...}`. This util masks anything that still
 * looks like a raw provider error and caps length so it always renders cleanly.
 */
export function formatInvitationError(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;

  // Mask anything that smells like a raw provider payload.
  const looksRaw =
    /^Resend\s+\d{3}/i.test(s) ||
    /"statusCode"\s*:\s*\d{3}/.test(s) ||
    /validation_error/i.test(s) ||
    s.startsWith('{') ||
    s.startsWith('[');

  if (looksRaw) {
    return "Couldn't deliver — please retry or contact support.";
  }

  // Already friendly — just cap length.
  return s.length > 140 ? `${s.slice(0, 137)}…` : s;
}

/**
 * Map an edge-function `manage-review-invitation` error payload to a
 * user-facing toast message. Falls back to a generic copy when the code
 * is unknown or the payload is malformed.
 */
export function formatInvitationActionError(payload: unknown, fallback?: string): string {
  const generic = fallback ?? 'Something went wrong. Please try again.';
  if (!payload || typeof payload !== 'object') return generic;
  const p = payload as { code?: unknown; error?: unknown; message?: unknown };
  const code = typeof p.code === 'string' ? p.code : typeof p.error === 'string' ? p.error : '';
  switch (code) {
    case 'PRIVATE_PAPER_NOT_SUPPORTED':
    case 'private_paper_not_supported':
      return "This paper is a confidential draft. Review invitations aren't available for confidential drafts yet — make it public, or wait for the upcoming private-share feature.";
    default:
      return typeof p.message === 'string' && p.message.trim().length > 0 ? p.message : generic;
  }
}
